// File: src/components/Sidebar.jsx
// -----------------------------------------------------------------------------
// Sidebar – desktop + mobile overlay version
// -----------------------------------------------------------------------------
import React, { useContext, useState, useEffect, useRef } from 'react';
import { Menu, LogOut, LayoutGrid, ChevronDown, MessageCircle } from 'lucide-react';
import { api } from '../lib/apiClient';
import SidebarItem from './ui/SidebarItem';
import { AuthContext } from '../context/AuthContext';
import { ProsContext } from "../context/ProsContext";
import { ConversationsContext } from "../context/ConversationsContext";
import PremiumModal from "../components/premium/PremiumModal";
import TivComingSoonModal from "../components/modals/TivComingSoonModal";
import useIsDesktop from "../hooks/useIsDesktop";
import { motion } from 'framer-motion';
import { NavLink } from 'react-router-dom';
import { QUIZZES } from '../data/quizzes';

const TopControls = ({onToggle}) => (
  <div className="flex items-center justify-between p-3 border-b border-black/10">
    <button
      onClick={onToggle}
      className="p-2 hover:bg-gray-900 rounded-xl"
      aria-label="Basculer la largeur"
    >
      <Menu size={20} className="text-zinc-100" />
    </button>
  </div>
);

// Animation variants pour les sections collapsibles - opacité seulement
const sectionVariants = {
  open: {
    opacity: 1,
    transition: { duration: 0.2 }
  },
  closed: {
    opacity: 0,
    transition: { duration: 0.15 }
  }
};

// Composant pour les titres de section cliquables
const SectionHeader = ({ title, emoji, isOpen, onClick }) => (
  <button
    onClick={onClick}
    className="px-5 w-full flex items-center justify-between text-sm font-semibold text-zinc-400 mt-6 mb-2 uppercase hover:text-zinc-300 transition-colors cursor-pointer"
  >
    <span><span className='mr-3'>{emoji}</span>{title}</span>
    <motion.span
      animate={{ rotate: isOpen ? 180 : 0 }}
      transition={{ duration: 0.2 }}
    >
      <ChevronDown size={16} />
    </motion.span>
  </button>
);

// const JOB_DIP_ALLOWED_IDS = new Set([93, 115, 175, 182, 183, 184]);

const ScrollArea = ({itemsPro, setMobileOpen, user, userPronoun, collapsed, prosOpen, setProsOpen, meConnaitreOpen, setMeConnaitreOpen, openPremiumModal, setShowTivModal}) => (
  <div className="overflow-y-auto mb-4 flex-1 px-3 flex-col flex gap-2" style={{ scrollbarWidth: 'thin', scrollbarColor: '#52525b transparent' }}>
    {!collapsed && (
      <>
        <NavLink
          to="/mon-espace"
          className={({ isActive }) =>
            [
              'mt-2 md:mt-0 flex items-center gap-3 px-4 py-2 rounded-xl transition',
              isActive ? 'bg-gray-900 text-white' : 'text-zinc-300 hover:bg-gray-900',
            ].join(' ')
          }
          onClick={() => setMobileOpen(false)}
          aria-label="Mon espace"
        >
          <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0">
            🏠
          </div>
          <span className="truncate leading-tight">Mon espace</span>
        </NavLink>
        <NavLink
          to="/chat/lux"
          className={({ isActive }) =>
            [
              'mt-2 flex items-center gap-3 px-4 py-2 rounded-xl transition',
              isActive ? 'bg-gray-900 text-white' : 'text-zinc-300 hover:bg-gray-900',
            ].join(' ')
          }
          onClick={() => setMobileOpen(false)}
          aria-label="Lux"
        >
          <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-base font-bold flex-shrink-0" style={{ background: "#7c3aed" }}>
            L
          </div>
          <span className="truncate leading-tight">Lux<br /><span className='text-xs text-gray-500'>Ton coach virtuel</span></span>
        </NavLink>
        {user?.school_level === 'terminale' && (
          <NavLink
            to={!user?.is_premium ? '#' : '/chat/v2/sup'}
            className={({ isActive }) =>
              [
                'mt-2 flex items-center gap-3 px-4 py-2 rounded-xl transition',
                !user?.is_premium
                  ?'opacity-30'
                    : (isActive ? 'bg-gray-900 text-white' : 'text-zinc-300 hover:bg-gray-900'),
              ].join(' ')
            }
            onClick={(e) => {
              if (!user?.is_premium) {
                e.preventDefault()
                openPremiumModal("discuter avec Sup", "Sup est réservé aux membres Premium")
              } else {
                setMobileOpen(false)
              }
            }}
            aria-label="Sup"
          >
          <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-base font-bold flex-shrink-0" style={{ background: "#7C6B4A" }}>
            S
          </div>
          <span className="truncate leading-tight">Sup<br /><span className='text-xs text-gray-500'>Spécialiste Parcoursup</span></span>
          </NavLink>
        )}
        {user?.school_level === 'terminale' && (
          <NavLink
            to={!user?.is_premium ? '#' : '/dip'}
            className={({ isActive }) =>
              [
                'mt-2 flex items-center gap-3 px-4 py-2 rounded-xl transition',
                !user?.is_premium
                  ?'opacity-30'
                    : (isActive ? 'bg-gray-900 text-white' : 'text-zinc-300 hover:bg-gray-900'),
              
              ].join(' ')
            }
            onClick={(e) => {
              if (!user?.is_premium) {
                e.preventDefault()
                openPremiumModal("explorer les formations", "DIP est réservé aux membres Premium")
              } else {
                setMobileOpen(false)
              }
            }}
            aria-label="Dip"
          >
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-base font-bold flex-shrink-0" style={{ background: "#0d9488" }}>
              D
            </div>
            <span className="truncate leading-tight">Dip<br /><span className='text-xs text-gray-500'>Explorer les formations</span></span>
            <span className="text-[10px] bg-teal-500/50 text-white px-2 py-0.5 rounded-full font-medium">
              Bêta
            </span>
          </NavLink>
        )}
        {user?.school_level === 'terminale' && (
          <NavLink
            to={!user?.is_premium ? '#' : '/tiv'}
            className={({ isActive }) =>
              [
                'mt-2 flex items-center gap-3 px-4 py-2 rounded-xl transition',
                !user?.is_premium
                  ?'opacity-30'
                    : (isActive ? 'bg-gray-900 text-white' : 'text-zinc-300 hover:bg-gray-900'),
              
              ].join(' ')
            }
            onClick={(e) => {
              if (!user?.is_premium) {
                e.preventDefault()
                openPremiumModal("faire ses lettres de motivation avec Tiv", "TIV est réservé aux membres Premium")
              } else {
                setMobileOpen(false)
              }
            }}
            aria-label="Tiv"
          >
            <div className="w-10 h-10 rounded-full flex items-center justify-center text-white text-base font-bold flex-shrink-0" style={{ background: "#2563eb" }}>
              T
            </div>
            <span className="truncate leading-tight flex-1">
              Tiv<br /><span className='text-xs text-gray-500'>Lettres de motivation</span>
            </span>
          </NavLink>
        )}
        <NavLink
          to="/job"
          className={({ isActive }) =>
            [
              'mt-2 flex items-center gap-3 px-4 py-2 rounded-xl transition',
              isActive ? 'bg-gray-900 text-white' : 'text-zinc-300 hover:bg-gray-900',
            ].join(' ')
          }
          onClick={() => setMobileOpen(false)}
          aria-label="Explorer les métiers"
        >
          <LayoutGrid size={20} />
          <span className="truncate">Explorer les métiers</span>
        </NavLink>

        {/* Section Me connaître */}
        {/* <SectionHeader
          title="Me connaître"
          emoji="✨"
          isOpen={meConnaitreOpen}
          onClick={() => { setMeConnaitreOpen(prev => !prev); setProsOpen(false); }}
        />
        {meConnaitreOpen && (
          <motion.div
            variants={sectionVariants}
            initial="closed"
            animate="open"
            exit="closed"
            className="pl-5 min-h-[120px] max-h-48 overflow-y-auto overflow-x-hidden pr-1 flex flex-col gap-1"
            style={{ scrollbarWidth: 'thin', scrollbarColor: '#52525b transparent' }}
          >
            {QUIZZES.map(q => {
              const locked = q.premium && !user?.is_premium;
              return (
                <NavLink
                  key={q.id}
                  to={locked ? '#' : q.path}
                  className={({ isActive }) =>
                    [
                      'flex items-center gap-3 px-4 py-2 rounded-xl transition',
                      locked ? 'opacity-30' : (isActive ? 'bg-gray-900 text-white' : 'text-zinc-300 hover:bg-gray-900'),
                    ].join(' ')
                  }
                  onClick={(e) => {
                    if (locked) {
                      e.preventDefault();
                      openPremiumModal(q.premiumReason(userPronoun), "Ce questionnaire est réservé aux membres Premium");
                    } else {
                      setMobileOpen(false);
                    }
                  }}
                  aria-label={`Mes ${q.label.toLowerCase()}`}
                >
                  <span className="truncate"><span className='mr-2'>{q.emoji}</span>Mes {q.label.toLowerCase()}</span>
                </NavLink>
              );
            })}
          </motion.div>
        )} */}

        {/* Section Professionnels */}
        {Object.entries(itemsPro).length > 0 && (
          <>
            <SectionHeader
              title="Professionnels"
              emoji="💬"
              isOpen={prosOpen}
              onClick={() => { setProsOpen(prev => !prev); setMeConnaitreOpen(false); }}
            />
            {prosOpen && (
              <motion.div
                variants={sectionVariants}
                initial="closed"
                animate="open"
                exit="closed"
                className="min-h-[100px] max-h-48 overflow-y-auto overflow-x-hidden pr-1"
                style={{ scrollbarWidth: 'thin', scrollbarColor: '#52525b transparent' }}
              >
                {Object.entries(itemsPro).map((item) => (
                  item[0] != 'lux' && item[0] != 'dip' && item[0] != 'sup' && <SidebarItem onClick={() => setMobileOpen(false)} key={item[0]} id={item[0]} title={item[1].title} gender={item[1].gender} />
                ))}
              </motion.div>
            )}
          </>
        )}
      </>
    )}
  </div>
);

const CHOICES = [
  { key: 'bug',      emoji: '😤', label: 'Ça bugge' },
  { key: 'idea',     emoji: '💡', label: 'J\'ai une idée' },
  { key: 'question', emoji: '🤔', label: 'Question' },
];

function SupportModal({ onClose }) {
  const [choice, setChoice]     = useState(null);
  const [text, setText]         = useState('');
  const [success, setSuccess]   = useState(false);
  const [sending, setSending]   = useState(false);

  const handleSend = async () => {
    if (!choice || !text.trim()) return;
    setSending(true);
    try {
      await api.post('/support', { message: `[${choice}] ${text.trim()}` });
      setSuccess(true);
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setChoice(null);
    setText('');
    setSuccess(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" onClick={handleClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div
        className="relative bg-gray-800 rounded-2xl w-full max-w-sm p-6 flex flex-col gap-4"
        onClick={e => e.stopPropagation()}
      >
        {!success ? (
          <>
            <div>
              <h3 className="text-white font-semibold text-lg">Dis-nous 👋</h3>
              <p className="text-zinc-400 text-sm mt-1">Stéphane, un des créateurs d'Oria, te répond</p>
            </div>

            <div className="flex gap-3">
              {CHOICES.map(c => (
                <button
                  key={c.key}
                  onClick={() => setChoice(c.key)}
                  className={[
                    'flex-1 flex flex-col items-center gap-1 py-3 rounded-xl border transition',
                    choice === c.key
                      ? 'border-indigo-500 bg-indigo-500/20 text-white'
                      : 'border-zinc-700 text-zinc-400 hover:border-zinc-500',
                  ].join(' ')}
                >
                  <span className="text-2xl">{c.emoji}</span>
                  <span className="text-xs font-medium">{c.label}</span>
                </button>
              ))}
            </div>

            {choice && (
              <textarea
                autoFocus
                value={text}
                onChange={e => setText(e.target.value)}
                placeholder="Raconte..."
                rows={4}
                className="w-full bg-gray-900 text-zinc-100 placeholder-zinc-500 rounded-xl px-4 py-3 text-sm resize-none outline-none focus:ring-1 focus:ring-indigo-500"
              />
            )}
            {choice && (
              <p className="text-zinc-500 text-xs -mt-2">
                Tes messages restent entre toi et l'équipe Oria. Tes parents ne voient pas ça.
              </p>
            )}

            <div className="flex gap-3 justify-end">
              <button
                onClick={handleClose}
                className="px-4 py-2 rounded-xl text-zinc-400 hover:text-zinc-200 text-sm transition"
              >
                Annuler
              </button>
              <button
                onClick={handleSend}
                disabled={!choice || !text.trim() || sending}
                className="px-4 py-2 rounded-xl bg-indigo-600 text-white text-sm font-medium transition disabled:opacity-40 hover:bg-indigo-500 disabled:cursor-not-allowed"
              >
                {sending ? 'Envoi...' : 'Envoyer'}
              </button>
            </div>
          </>
        ) : (
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <span className="text-5xl">🤙</span>
            <h3 className="text-white font-semibold text-lg">C'est envoyé</h3>
            <p className="text-zinc-400 text-sm">Stéphane te répond dès qu'il peut. Merci d'avoir pris le temps !</p>
            <button
              onClick={handleClose}
              className="mt-2 px-6 py-2 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-500 transition"
            >
              OK
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const LogoutButton = ({ onClick, collapsed, mobileOpen }) => (
  <div className="mt-auto px-2 pb-2 w-full">
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-3 py-2 rounded-xl text-zinc-300 hover:bg-gray-900 transition-colors w-full"
    >
      <LogOut size={18} />
      {(!collapsed || mobileOpen ) && <span className="truncate">Déconnexion</span>}
    </button>
  </div>
);

export default function Sidebar({ collapsed, onToggle }) {
  /* ----------------------------------------------------------------------- */
  /* State & context                                                         */
  /* ----------------------------------------------------------------------- */
  const { itemsPro }                              = useContext(ProsContext);
  const { resetSession }                          = useContext(ConversationsContext);
  const { logout, user, updateUser }              = useContext(AuthContext);
  const [mobileOpen, setMobileOpen]               = useState(false);
  const [openWidth, setOpenWidth]                 = useState(260);
  const [prosOpen, setProsOpen]                   = useState(false);
  const [meConnaitreOpen, setMeConnaitreOpen]     = useState(true);
  const isDesktop                                 = useIsDesktop();
  const asideRef                                  = useRef(null);
  const [showPremiumModal, setShowPremiumModal]   = useState(false);
  const [premiumModalReason, setPremiumModalReason] = useState('');
  const [premiumModalContext, setPremiumModalContext] = useState('');
  const [showTivModal, setShowTivModal]           = useState(false);
  const [showSupportModal, setShowSupportModal]   = useState(false);

  const handleLogoutClick = () => {
    resetSession();
    logout();
  }

  const openPremiumModal = (reason, context) => {
    setPremiumModalReason(reason);
    setPremiumModalContext(context || '');
    setShowPremiumModal(true);
  }

  useEffect(() => {
    if (!asideRef.current) return
    const aside = asideRef.current;
    setOpenWidth(aside.getBoundingClientRect().width)
  }, [asideRef]);

  useEffect(() => {
    if ( !isDesktop ) {
      document.documentElement.style.setProperty(
        "--sidebar-w",
        `0px`
      );
    } else {
      const val = collapsed ? 60 : openWidth;
      document.documentElement.style.setProperty(
        "--sidebar-w",
        `${val}px`
      );
    }
  }, [isDesktop, collapsed, openWidth]);
  /* ----------------------------------------------------------------------- */
  /*  Desktop sidebar (hidden on < md)                                       */
  /* ----------------------------------------------------------------------- */
  const DesktopSidebar = (
    isDesktop && (
      <motion.aside
        ref={asideRef}
        animate={{ width: collapsed ? 60 : 260 }}
        className="hidden md:flex h-full bg-gray-800 flex-col transition-all overflow-hidden"
      >
        <TopControls onToggle={onToggle}/>
        <ScrollArea
          itemsPro={itemsPro}
          setMobileOpen={setMobileOpen}
          user={user}
          userPronoun={user.gender === 'boy' ? 'le': 'la'}
          collapsed={collapsed}
          prosOpen={prosOpen}
          setProsOpen={setProsOpen}
          meConnaitreOpen={meConnaitreOpen}
          setMeConnaitreOpen={setMeConnaitreOpen}
          openPremiumModal={openPremiumModal}
          setShowTivModal={setShowTivModal}
        />
        {!collapsed && (
          <>
            <hr className='border-t border-gray-700'/>
            <button
              onClick={() => setShowSupportModal(true)}
              className="mx-3 mt-4 mb-2 flex items-center gap-3 px-3 py-3 rounded-xl bg-gray-700/50 hover:bg-gray-700 transition text-left"
            >
              <div className="w-8 h-8 rounded-full bg-white/50 flex items-center justify-center shrink-0">
                <MessageCircle size={16} />
              </div>
              <div className="min-w-0">
                <p className="text-zinc-200 text-xs font-semibold leading-tight">Un truc qui marche pas ?</p>
                <p className="text-zinc-500 text-xs leading-tight truncate">Dis-le nous,<br />Stéphane te répond</p>
              </div>
            </button>
          </>
        )}
        <LogoutButton onClick={handleLogoutClick} collapsed={collapsed} mobileOpen={mobileOpen} />
      </motion.aside>
    )
  );
  /* ----------------------------------------------------------------------- */
  /*  Mobile overlay                                                         */
  /* ----------------------------------------------------------------------- */
  const MobileSidebar = (
    mobileOpen && (
      <div className="fixed inset-0 z-40 flex md:hidden">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/60"
          onClick={() => setMobileOpen(false)}
        />

        {/* Drawer */}
        <aside className="relative w-64 h-full bg-gray-800 flex flex-col">
          {/* <div className="flex items-center justify-between p-5">
            <button
              onClick={() => setMobileOpen(false)}
              className="fixed top-1.5 left-3 z-50 rounded-full text-zinc-100 shadow-lg md:hidden"
              aria-label="Fermer le menu"
            >
              <Menu size={20} className="text-zinc-100" />
            </button>
          </div> */}

          {/* Dans l'overlay mobile on réutilise ScrollArea */}
          <ScrollArea
            itemsPro={itemsPro}
            setMobileOpen={setMobileOpen}
            user={user}
            collapsed={false}
            prosOpen={prosOpen}
            setProsOpen={setProsOpen}
            meConnaitreOpen={meConnaitreOpen}
            setMeConnaitreOpen={setMeConnaitreOpen}
            openPremiumModal={openPremiumModal}
            setShowTivModal={setShowTivModal}
          />

          <hr className='border-t border-gray-700'/>
          <button
            onClick={() => setShowSupportModal(true)}
            className="mx-3 mt-2 mb-2 flex items-center gap-3 px-3 py-3 rounded-xl bg-gray-700/50 hover:bg-gray-700 transition text-left"
          >
            <div className="w-8 h-8 rounded-full bg-white/50 flex items-center justify-center shrink-0">
              <MessageCircle size={16} />
            </div>
            <div className="min-w-0">
              <p className="text-zinc-200 text-xs font-semibold leading-tight">Un truc qui marche pas ?</p>
              <p className="text-zinc-500 text-xs leading-tight truncate">Dis-le nous,<br />Stéphane te répond</p>
            </div>
          </button>
          <LogoutButton
            onClick={() => {
              logout();
              setMobileOpen(false);
            }}
            collapsed={collapsed}
            mobileOpen={mobileOpen}
          />
        </aside>
      </div>
    )
  );

  /* ----------------------------------------------------------------------- */
  /*  Mobile hamburger button                                                */
  /* ----------------------------------------------------------------------- */
  const MobileHamburger = (
    !mobileOpen && (
      <button
        aria-label="Ouvrir le menu"
        onClick={() => setMobileOpen(true)}
        className="fixed top-1.5 left-3 z-50 rounded-full text-zinc-100 shadow-lg md:hidden"
      >
        <Menu size={20} />
      </button>
    )
  );

  /* ----------------------------------------------------------------------- */
  /*  Render                                                                 */
  /* ----------------------------------------------------------------------- */
  return (
    <>
      {MobileHamburger}
      {MobileSidebar}
      {DesktopSidebar}

      
      <PremiumModal
        isOpen={showPremiumModal}
        onClose={() => setShowPremiumModal(false)}
        askedForPremium={user?.asked_for_premium}
        onUpdateUser={updateUser}
        context={premiumModalContext}
        reason={premiumModalReason}
      />

      <TivComingSoonModal
        isOpen={showTivModal}
        onClose={() => setShowTivModal(false)}
      />

      {showSupportModal && <SupportModal onClose={() => setShowSupportModal(false)} />}
    </>
  );
}
